<template>
  <Card class="py-6 w-full px-3">
    <div v-if="!showError">
      <!-- Informational Alert -->
      <div class="mb-6 bg-[#FFF9E6] border border-[#FFE5B4] rounded-lg p-4 flex items-start gap-3" style="background-color: #FFF9E6; border-color: #FFE5B4; border-radius: 8px; padding: 12px; display: flex; align-items: center; gap: 8px;">
        <Icon icon-name="bulb-alert" class="flex-shrink-0" style="fill: transparent; width: 24px; height: 24px;" />
        <p class="text-sm text-[#E99323] leading-relaxed">
          Withholding tax applies only to employees engaged as independent contractors, whether operating as individuals or registered businesses.
        </p>
      </div>

      <!-- Enable Withholding Tax Deductions Toggle -->
      <div class="mb-8">
        <div class="flex items-center gap-3">
          <label class="switch">
            <input
              type="checkbox"
              :checked="withholdingTaxEnabled"
              @change="handleToggleChange($event.target.checked)"
            />
            <span class="slider round"></span>
          </label>
          <p class="text-sm font-semibold text-gray-700">Enable withholding tax deductions</p>
        </div>
      </div>

      <!-- Tax Rate Configuration Section -->
      <div >

        <!-- Tax Rates Table -->
        <Card class="p-0">
        <h3 class="text-base px-4 pt-4 font-bold text-gray-800 mb-2 uppercase tracking-wider" style="font-weight: 600;">
          TAX RATE CONFIGURATION
        </h3>
        <p class="text-sm px-4  text-gray-600 mb-6">
          Define withholding tax rates for the different categories of contractors.
        </p>
          <sTable
            :headers="taxRatesHeader"
            :items="taxRatesData"
            aria-label="tax rates table"
            class="w-full"
            :has-checkbox="false"
            :has-number="false"
            :loading="false"
          >
            <template v-slot:item="{ item }">
              <span v-if="item.contractorCategory" class="text-m font-medium text-gray-700">
                {{ item.data.contractorCategory.data }}
              </span>
              <span v-if="item.rate" class="flex items-center">
                <c-text
                  v-model.number="item.data.rate.data"
                  variant="w-20"
                  type="number"
                  disabled
                  placeholder="0.0"
                  step="0.1"
                  min="0"
                  max="100"
                />
                <span class="ml-2 text-sm font-medium text-gray-700">%</span>
              </span>
            </template>
          </sTable>
        </Card>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-4 mt-8">
        <Button
          :class="`bg-dynamicBackBtn text-white`"
          @click="saveChanges"
        >
          Save Changes
        </Button>
        <Button
          class="text-darkPurple bg-white border border-gray-300"
          @click="revertChanges"
          :disabled="!withholdingTaxEnabled"
        >
          Revert Changes
        </Button>
      </div>
    </div>
    <div v-else>
      <error-page />
    </div>
  </Card>
</template>

<script>
import Card from "@/components/Card";
import Button from "@/components/Button";
import Icon from "@/components/Icon";
import ErrorPage from "@/modules/Admin/error403";
import STable from "@scelloo/cloudenly-ui/src/components/table";
import CText from "@scelloo/cloudenly-ui/src/components/text";

export default {
  components: {
    Card,
    Button,
    Icon,
    ErrorPage,
    STable,
    CText
  },
  props: {
    orgPaySettingId: {
      type: [String, Number],
      default: null
    },
    isActive: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showError: false,
      withholdingTaxEnabled: false,
      taxRatesHeader: [
        { title: "CONTRACTOR CATEGORY", value: "contractorCategory" },
        { title: "RATE", value: "rate" },
      ],
      taxRatesData: [
        {
          contractorCategory: { data: "Resident Individual" },
          rate: { data: 5 },
        },
        {
          contractorCategory: { data: "Non Resident Individual" },
          rate: { data: 10 },
        },
      ],
      originalTaxRates: [
        {
          contractorCategory: { data: "Resident Individual" },
          rate: { data: 5 },
        },
        {
          contractorCategory: { data: "Non Resident Individual" },
          rate: { data: 10 },
        },
      ],
    };
  },
  methods: {
    handleToggleChange(newValue) {
      this.withholdingTaxEnabled = newValue;
      if (!newValue) {
        this.taxRatesData = JSON.parse(JSON.stringify(this.originalTaxRates));
      }
    },
    getTaxRatesObject() {
      // Convert array format to object for API
      const rates = {};
      this.taxRatesData.forEach((item) => {
        const category = item.contractorCategory.data;
        if (category === "Resident Individual") {
          rates.residentIndividual = item.rate.data;
        } else if (category === "Non Resident Individual") {
          rates.nonResidentIndividual = item.rate.data;
        }
      });
      return rates;
    },
    async saveChanges() {
      try {
        const taxRates = this.getTaxRatesObject();
        const orgPaySettingId = this.orgPaySettingId || this.$store.state.paySettingsActiveIds.ACTIVE || null;

        await this.$_saveWithholdingTaxSettings({
          orgPaySettingId,
          residentIndividual: taxRates.residentIndividual,
          nonResidentIndividual: taxRates.nonResidentIndividual,
          isActive: this.withholdingTaxEnabled,
        });

        this.originalTaxRates = JSON.parse(JSON.stringify(this.taxRatesData));
        this.$toasted.success("Settings saved", {
          duration: 5000,
        });
      } catch (error) {
        this.$toasted.error(error.message || "Settings not saved", {
          duration: 5000,
        });
      }
    },
    revertChanges() {
      this.taxRatesData = JSON.parse(JSON.stringify(this.originalTaxRates));
      this.$toasted.success("Settings reverted", {
        duration: 5000,
      });
    },
    async loadWithholdingTaxSettings() {
      try {
        const orgPaySettingId = this.$store.state.paySettingsActiveIds.ACTIVE || null;
        const { data } = await this.$_getWithholdingTaxSettings({ orgPaySettingId });
        if (data && data.setting) {
          this.withholdingTaxEnabled = data.setting.isActive || false;
          this.taxRatesData = [
            {
              contractorCategory: { data: "Resident Individual" },
              rate: { data: data.setting.residentIndividual || 5 },
            },
            {
              contractorCategory: { data: "Non Resident Individual" },
              rate: { data: data.setting.nonResidentIndividual || 10 },
            },
          ];
          this.originalTaxRates = JSON.parse(JSON.stringify(this.taxRatesData));
        }
      } catch (error) {
        this.$toasted.error(error.message || "Failed to load withholding tax settings", {
          duration: 5000,
        });
      }
    },
  },
   watch: {
     isActive(newVal) {
       if (newVal) {
         this.loadWithholdingTaxSettings();
       }
     },
     orgPaySettingId: {
       handler() {
         if (this.isActive) {
           this.loadWithholdingTaxSettings();
         }
       },
       immediate: false
     }
   },
   async mounted() {
     try {
       await this.$handlePrivilege("paySettings", "accessComplianceSettings");
       this.showError = false;
       if (this.isActive) {
         this.loadWithholdingTaxSettings();
       }
     } catch (error) {
       this.showError = true;
     }
   },
};
</script>

<style scoped>
.border__dashed {
  height: 0px;
  margin: 18px 0px;
  border: 1px dashed #878e99;
}
.switch {
  position: relative;
  display: inline-block;
  width: 24px;
  height: 12px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #EEEEEE;
  transition: 0.4s;
  border-radius: 20px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 9.6px;
  width: 9.6px;
  border-radius: 50%;
  left: 2px;
  bottom: 1.5px;
  background-color: #878E99;
  transition: 0.4s;
}

input:checked + .slider {
  background-color: #EEEEEE;
}

input:checked+.slider:before {
  transform: translateX(10px);
  background-color:#13B56A;
}

.switch-header {
  font-size: 10px;
  text-transform: uppercase;
  font-weight: 600;
  color: #878E99;
}
</style>
